import { useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, X, Globe } from "lucide-react";

// Per-client portal branding: display name, accent colour, logo (stored as a self-contained
// data URL so the portal renders it without an auth header), and an informational portal domain.
export function BrandingDialog({ wid, ws, open, onOpenChange, onSaved }) {
  const b = ws?.branding || {};
  const [name, setName] = useState(b.name || ws?.organization_name || "");
  const [accent, setAccent] = useState(b.accent || "#0f766e");
  const [logo, setLogo] = useState(b.logo || null);
  const [domain, setDomain] = useState(b.domain || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const pick = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (f.size > 500 * 1024) return toast.error("Logo must be under 500 KB");
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/clients/${wid}`, { branding: { ...b, name: name.trim() || ws?.organization_name, accent, logo, domain: domain.trim() } });
      toast.success("Branding updated — clients see it on next portal load");
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-w-md" data-testid="branding-dialog">
      <DialogHeader><DialogTitle>Portal branding</DialogTitle><DialogDescription className="sr-only">Set this client's portal display name, accent colour, logo and domain.</DialogDescription></DialogHeader>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden text-white font-heading font-bold text-xl shrink-0" style={{ background: accent }}>
            {logo ? <img src={logo} alt="logo" className="w-full h-full object-cover" data-testid="branding-logo-preview" /> : (name || "C")[0]}
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="branding-logo-input" />
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="branding-logo-btn"><Upload className="w-4 h-4 mr-1" /> {logo ? "Replace logo" : "Upload logo"}</Button>
            {logo && <Button size="sm" variant="ghost" className="rounded-full text-slate-500" onClick={() => setLogo(null)} data-testid="branding-logo-remove"><X className="w-4 h-4" /></Button>}
          </div>
        </div>
        <div><label className="text-xs font-medium text-slate-600">Display name</label><input value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--line, #e5e7eb)" }} data-testid="branding-name-input" /></div>
        <div><label className="text-xs font-medium text-slate-600">Accent colour</label><div className="flex items-center gap-2 mt-1"><input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="h-9 w-12 rounded border cursor-pointer" data-testid="branding-accent-input" /><input value={accent} onChange={(e) => setAccent(e.target.value)} className="flex-1 rounded-lg border px-3 py-2 text-sm font-mono" style={{ borderColor: "var(--line, #e5e7eb)" }} /></div></div>
        <div>
          <label className="text-xs font-medium text-slate-600 flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Portal domain <span className="text-slate-400 font-normal">(optional)</span></label>
          <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="portal.yourclient.com" className="w-full mt-1 rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--line, #e5e7eb)" }} data-testid="branding-domain-input" />
          <p className="text-[11px] text-slate-400 mt-1">Record the domain you want for this portal. To make it live, point it at your deployed app under <b>Deploy → Custom domain</b>. The portal already works on your main app URL today.</p>
        </div>
        <div className="flex justify-end gap-2 pt-1"><Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button><Button className="rounded-full" onClick={save} disabled={busy} data-testid="branding-save">{busy ? "Saving…" : "Save branding"}</Button></div>
      </div>
    </DialogContent></Dialog>
  );
}
